import React, { FC, useState, useCallback } from 'react';
import clientService, { AutoCompleteSuggestion } from '../../services/client.service';
import { AutoCompleteTextField } from 'shamrock-clover-ui/dist/clover/components/AutoCompleteTextField/AutoCompleteTextField';
import { debounceAsync } from '../../utils/utils';

interface Props {
  onOptionSelect?: Function;
  unsetValueOnSelect?: boolean;
  disabledSuggestions?: AutoCompleteSuggestion[];
  selectedValue?: string;
  onSuggestionError?: string;
}

const ClientAutoSuggestion: FC<Props> = ({
  onOptionSelect,
  unsetValueOnSelect = false,
  disabledSuggestions = [],
  selectedValue,
  onSuggestionError,
}) => {
  const [searchText, setSearchText] = useState(selectedValue ? selectedValue : '');

  const MIN_CHAR_COUNT = 3;

  const columns = [
    {
      key: 'client_number',
      label: 'Client Number',
      width: 90,
      useAsDisabledSuggestionsKey: true,
    },
    {
      key: 'client_name',
      label: 'Client Name',
      width: 280,
    },
    {
      key: 'mc_number',
      label: 'MC #',
      width: 60,
    },
  ];

  const clearSearch = () => {
    setSearchText('');
  };

  const getViewAsSuggestions = useCallback(
    debounceAsync(async (searchVal) => {
      return clientService.getClientAutoComplete(searchVal);
    }, 600),
    [],
  );

  const doesExclusionMatch = (exclusion: Partial<AutoCompleteSuggestion>, suggestion: AutoCompleteSuggestion) => {
    const matchCol = columns.find((col) => col.useAsDisabledSuggestionsKey);
    return matchCol && exclusion[matchCol.key] === suggestion[matchCol.key];
  };

  const pickSelection = (selection) => {
    const currentSearchText = searchText;
    const isDisabled = disabledSuggestions.length
      ? disabledSuggestions.some((exclusion) => doesExclusionMatch(exclusion, selection))
      : false;

    if (!isDisabled) {
      if (onOptionSelect) {
        onOptionSelect(selection);
      }
      if (unsetValueOnSelect) {
        setSearchText('');
      } else {
        setSearchText(selection.client_name);
      }
    } else {
      setSearchText(currentSearchText);
    }
  };

  const handleFocus = (event) => event.target.select();

  return (
    <AutoCompleteTextField
      suggestionError={onSuggestionError}
      className={'gbunmask'}
      value={searchText}
      noSuggestionsFoundMessage={'No accounts match your search.'}
      label={'Search by Client #, Name, or MC #'}
      getSuggestions={onSuggestionError ? null : getViewAsSuggestions}
      minCharCount={onSuggestionError ? 0 : MIN_CHAR_COUNT}
      maxResultsDisplayed={100}
      selectSuggestion={(_event, { suggestion }) => {
        pickSelection(suggestion);
      }}
      suggestions={[]}
      disabledSuggestions={disabledSuggestions}
      type={'columned'}
      onChange={(e: any) => setSearchText(e.target.value)}
      columns={columns}
      onFocus={handleFocus}
      clearSearch={clearSearch}
    />
  );
};

export default ClientAutoSuggestion;
